/**
 * FreeDialerWidget — Marcador flotante para llamadas libres vía Telnyx WebRTC.
 *
 * Permite al agente marcar cualquier número directamente desde el navegador.
 * Al pulsar "Llamar", registra la llamada via orchestrator y abre CallSidebar
 * para el flujo completo (gathering + transcripción + resultado).
 *
 * Flujo:
 *   FAB cerrado → Abrir panel → Escribir número → Llamar → CallSidebar se abre
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { normalizePhoneE164 } from '@/utils/phoneUtils';
import { startCall as orchestratorStartCall, findContactByPhone } from '@/services/callOrchestrator';
import { useCallStore } from '@/stores/useCallStore';
import { logger } from '@/utils/logger';

// ─── Constantes ───────────────────────────────────────────────────────────────

const DIAL_KEYS: string[][] = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#'],
];

const DIAL_SUBLABELS: Record<string, string> = {
  '1': '', '2': 'ABC', '3': 'DEF',
  '4': 'GHI', '5': 'JKL', '6': 'MNO',
  '7': 'PRS', '8': 'TUV', '9': 'WXY',
  '0': '+', '*': '', '#': '',
};

// ─── FAB icon: teclado de marcación ──────────────────────────────────────────

function DialPadIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <circle cx="5"  cy="5"  r="1.8" />
      <circle cx="12" cy="5"  r="1.8" />
      <circle cx="19" cy="5"  r="1.8" />
      <circle cx="5"  cy="11" r="1.8" />
      <circle cx="12" cy="11" r="1.8" />
      <circle cx="19" cy="11" r="1.8" />
      <circle cx="5"  cy="17" r="1.8" />
      <circle cx="12" cy="17" r="1.8" />
      <circle cx="19" cy="17" r="1.8" />
    </svg>
  );
}

// ─── FreeDialerWidget ─────────────────────────────────────────────────────────

export function FreeDialerWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [calling, setCalling] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const callStoreIsOpen = useCallStore((s) => s.isOpen);

  // Preview del número normalizado E.164
  const normalizedPreview = useMemo(() => {
    const raw = phoneInput.trim();
    if (!raw || raw.length < 3) return '';
    return normalizePhoneE164(raw);
  }, [phoneInput]);

  // Focus en el input al abrir
  useEffect(() => {
    if (isOpen && !callStoreIsOpen) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [isOpen, callStoreIsOpen]);

  // Cerrar panel cuando CallSidebar se abre (la llamada empezó)
  useEffect(() => {
    if (callStoreIsOpen && isOpen) {
      setPhoneInput('');
      setCalling(false);
    }
  }, [callStoreIsOpen, isOpen]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleKey = useCallback((key: string) => {
    setPhoneInput((prev) => (prev + key).slice(0, 16));
  }, []);

  const handleBackspace = useCallback(() => {
    setPhoneInput((prev) => prev.slice(0, -1));
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^\d+\s\-()]/g, '');
    setPhoneInput(val.slice(0, 16));
  }, []);

  const handleCall = useCallback(async () => {
    const raw = phoneInput.trim();
    if (!raw || calling) return;
    setCalling(true);

    try {
      const phone = normalizePhoneE164(raw);

      // 1. Buscar contacto por teléfono
      let contactId: string | null = null;
      let contactName = '';
      try {
        const resolved = await findContactByPhone(phone);
        if (resolved) {
          contactId = resolved.contact_id;
          contactName = resolved.contact_name;
        }
      } catch {
        logger.warn('[FreeDialer] No se pudo resolver contacto por teléfono');
      }

      // 2. Registrar llamada via orchestrator
      const dialRes = await orchestratorStartCall({
        phone,
        origin: 'dialer',
        contactId: contactId ?? undefined,
      });

      // 3. Abrir CallSidebar con datos del contacto
      // Usar contact_id del orchestrator si el frontend no lo resolvió
      const resolvedContactId = contactId ?? dialRes.contact_id ?? '';
      const store = useCallStore.getState();
      store.startCall(
        {
          id: resolvedContactId,
          name: contactName || phone,
          phone,
          isFirstCall: dialRes.is_new_contact,
        },
        undefined,
        'dialer',
      );
      store.setActiveCallId(dialRes.call_id);

      // CallSidebar toma el control: WebRTC, transcripción, gathering, resultado
    } catch (err: any) {
      logger.error('[FreeDialer] Error al iniciar llamada:', err);
      toast.error(err?.response?.data?.detail ?? 'No se pudo iniciar la llamada');
      setCalling(false);
    }
  }, [phoneInput, calling]);

  const handleInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && phoneInput.trim()) {
      handleCall();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  }, [phoneInput, handleCall]);

  const handleClose = useCallback(() => {
    if (callStoreIsOpen) return; // No cerrar si hay llamada activa
    setPhoneInput('');
    setCalling(false);
    setIsOpen(false);
  }, [callStoreIsOpen]);

  const toggleOpen = useCallback(() => {
    if (isOpen) handleClose();
    else setIsOpen(true);
  }, [isOpen, handleClose]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* ── FAB ── */}
      <motion.button
        onClick={toggleOpen}
        whileTap={{ scale: 0.9 }}
        className={cn(
          'fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full shadow-xl transition-colors duration-200',
          callStoreIsOpen
            ? 'bg-green-500 ring-4 ring-green-300/60'
            : isOpen
            ? 'bg-gray-700'
            : 'bg-gray-800 hover:bg-gray-700',
        )}
        aria-label={isOpen ? 'Cerrar marcador' : 'Abrir marcador libre Telnyx'}
        title="Marcador libre (Telnyx WebRTC)"
      >
        {callStoreIsOpen ? (
          <motion.span
            animate={{ rotate: [0, -10, 10, 0] }}
            transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 1.5 }}
            className="text-2xl"
          >
            📞
          </motion.span>
        ) : (
          <DialPadIcon className="h-6 w-6 text-white" />
        )}
      </motion.button>

      {/* ── Panel marcador ── */}
      <AnimatePresence>
        {isOpen && !callStoreIsOpen && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="fixed bottom-24 right-6 z-50 w-72 rounded-2xl bg-white shadow-2xl border border-gray-100 overflow-hidden"
            role="dialog"
            aria-label="Marcador libre Telnyx"
          >
            {/* Cabecera */}
            <div className="flex items-center justify-between px-4 py-3 bg-gray-900 text-white">
              <div className="flex items-center gap-2">
                <DialPadIcon className="h-4 w-4 text-white/70" />
                <span className="text-sm font-semibold tracking-wide">Marcador libre</span>
                <span className="text-[10px] text-white/40 uppercase">Telnyx WebRTC</span>
              </div>
              <button
                type="button"
                onClick={handleClose}
                aria-label="Cerrar marcador"
                className="text-white/50 hover:text-white transition-colors p-0.5"
              >
                <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <div className="p-4 space-y-3">
              {/* Input de teléfono */}
              <div className="relative rounded-xl border-2 bg-gray-50 border-gray-100 flex items-center px-3 py-2">
                <input
                  ref={inputRef}
                  type="tel"
                  inputMode="tel"
                  value={phoneInput}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyDown}
                  placeholder="+34 600 000 000"
                  className="flex-1 bg-transparent text-center text-xl font-mono text-gray-900 placeholder-gray-300 tracking-widest outline-none min-w-0"
                  aria-label="Número a marcar"
                  autoComplete="off"
                />
                {phoneInput && (
                  <button
                    type="button"
                    onClick={handleBackspace}
                    aria-label="Borrar último dígito"
                    className="absolute right-3 text-gray-400 hover:text-gray-600 transition-colors text-lg"
                  >
                    ⌫
                  </button>
                )}
              </div>

              {/* Teclado numérico */}
              <div className="grid grid-cols-3 gap-1.5">
                {DIAL_KEYS.flat().map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleKey(key)}
                    className="flex flex-col items-center justify-center h-12 rounded-xl bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors"
                  >
                    <span className="text-gray-900 font-bold text-base leading-none">{key}</span>
                    {DIAL_SUBLABELS[key] && (
                      <span className="text-[9px] text-gray-400 font-medium tracking-widest mt-0.5">
                        {DIAL_SUBLABELS[key]}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Botón llamar */}
              <motion.button
                type="button"
                onClick={handleCall}
                disabled={!phoneInput.trim() || calling}
                whileTap={{ scale: 0.96 }}
                className="w-full h-14 rounded-2xl font-bold text-white text-base bg-green-500 hover:bg-green-600 active:bg-green-700 transition-all shadow-md disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                aria-label="Iniciar llamada"
              >
                <span className="flex items-center justify-center gap-2">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                    <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" />
                  </svg>
                  {calling ? 'Conectando…' : 'Llamar'}
                </span>
              </motion.button>

              {/* Preview del número normalizado */}
              {normalizedPreview && normalizedPreview !== phoneInput.trim() && (
                <p className="text-center text-[11px] text-blue-500 font-mono font-medium -mt-1">
                  → {normalizedPreview}
                </p>
              )}
              {!normalizedPreview && (
                <p className="text-center text-[10px] text-gray-300">
                  Sin prefijo → se asume +34 (España)
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
